import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { useSocket } from '../contexts/SocketContext';
import { useToast } from './Toast';
import { copyToClipboard } from '../utils/clipboard';
import { formatBJTime } from '../utils/datetime';
import { Wallet, TrendingUp, ChevronDown, ChevronUp, Tag, Bell, Copy } from 'lucide-react';

// 「我的」页的收益区：排五 + 证券 合并成一份。
//   · 蓝色收益卡：总收益 / 今日 / 本月（两个业务加总），点开看分业务单价、今日结算通知、近期结算
//   · 可提现余额卡 + 申请提现 / 提现记录弹窗（仅上级已参与 USDT 体系且本人已绑定收款地址时显示）
// 原来这些都在工作台里（排五、证券各一张收益卡，提现卡只在排五侧）；现在只在「我的」页显示，工作台不再放收益。
// refreshKey：「我的」页绑定 / 关闭收款地址后改变它，让余额卡跟着刷新。

const BIZ_LABEL = { legacy: '排五', puzzle: '证券' };
const BIZ_UNIT  = { legacy: '条', puzzle: '单' };

export default function EarningsPanel({ refreshKey }) {
  const { subscribe } = useSocket();
  const { showToast } = useToast();

  const [earnings, setEarnings] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [todaySettlements, setTodaySettlements] = useState([]);

  // USDT 提现
  const [wdBalance, setWdBalance] = useState(0);        // 可提现余额（已扣掉冻结的部分）
  const [wdFrozen, setWdFrozen] = useState(0);          // 冻结中：待审批 + 已审批等待到账
  const [wdAddress, setWdAddress] = useState(null);
  const [wdAvailable, setWdAvailable] = useState(false);
  const [wdModal, setWdModal] = useState(false);
  const [wdAmountCny, setWdAmountCny] = useState('');
  const [wdRate, setWdRate] = useState(null);
  const [wdRateErr, setWdRateErr] = useState(false);
  const [wdSubmitting, setWdSubmitting] = useState(false);
  const [wdHistory, setWdHistory] = useState(false);
  const [wdHistoryList, setWdHistoryList] = useState([]);

  const fetchEarnings = useCallback(() => {
    api.get('/settlement/my-earnings').then(r => setEarnings(r.data)).catch(() => {});
  }, []);
  const fetchTodaySettlements = useCallback(() => {
    api.get('/notifications/mine/settlement-today')
      .then(r => setTodaySettlements(r.data.list || []))
      .catch(() => {});
  }, []);
  const fetchWdInfo = useCallback(() => {
    api.get('/withdrawal/info').then(r => {
      setWdBalance(r.data.balance ?? 0);
      setWdFrozen(r.data.frozen_cny ?? 0);
      setWdAddress(r.data.trc20_address ?? null);
      setWdAvailable(r.data.available ?? false);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchEarnings(); fetchTodaySettlements(); }, [fetchEarnings, fetchTodaySettlements]);
  useEffect(() => { fetchWdInfo(); }, [fetchWdInfo, refreshKey]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event, payload) => {
      if (['settlement_updated', 'data_refresh', 'stats_refresh', 'puzzle_updated'].includes(event)) { fetchEarnings(); fetchWdInfo(); }
      if (event === 'withdrawal_updated') fetchWdInfo();
      // 排五结算不会给收款人单独发 socket 事件，只有这条「结算通知」推送，所以收益 / 余额也靠它刷新
      if (event === 'push_message' && payload && (payload.title === '结算通知' || String(payload.title).startsWith('结算通知｜'))) {
        fetchTodaySettlements();
        fetchEarnings();
        fetchWdInfo();
      }
    });
  }, [subscribe, fetchEarnings, fetchTodaySettlements, fetchWdInfo]);

  const copySettlement = async (content) => {
    await copyToClipboard(content);
    showToast('已复制结算通知内容', 'success');
  };

  const secTitle = { fontSize: 12, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 };

  const priceBox = (biz) => {
    const p = earnings?.by?.[biz]?.price;
    return (
      <div key={biz} style={{ flex: 1, background: 'var(--c-bg)', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginBottom: 4 }}>{BIZ_LABEL[biz]}</div>
        {p != null ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--c-primary)' }}>¥{Number(p).toFixed(2)}</div>
            <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginTop: 3 }}>单价 / {BIZ_UNIT[biz]}</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-text-tertiary)' }}>未设置单价</div>
            <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginTop: 3 }}>请联系负责人</div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* 收益卡（排五 + 证券 合计） */}
      {earnings && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 12 }}>
          <div className="gradient-blue"
            onClick={() => setShowDetail(v => !v)}
            style={{ color: 'white', padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.85, fontSize: 13, fontWeight: 500 }}>
                <Wallet size={14} /> 我的收益
                <span style={{ opacity: 0.6, fontSize: 11, marginLeft: 2 }}>{showDetail ? '收起' : '查看详情'}</span>
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, marginTop: 4, letterSpacing: '-0.02em' }}>
                ¥{(earnings.total || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                今日 ¥{(earnings.today || 0).toFixed(2)}　本月 ¥{(earnings.month || 0).toFixed(2)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={36} style={{ opacity: 0.2 }} />
              {showDetail ? <ChevronUp size={16} style={{ opacity: 0.7 }} /> : <ChevronDown size={16} style={{ opacity: 0.7 }} />}
            </div>
          </div>

          {showDetail && (
            <div style={{ padding: '14px 16px', background: 'var(--c-surface)' }}>
              {/* 今日结算通知：弹窗可能被误关，这里常驻展示 + 复制 */}
              {todaySettlements.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={secTitle}><Bell size={12} /> 今日结算通知</div>
                  {todaySettlements.map(s => (
                    <div key={s.id} style={{
                      background: 'rgba(52,199,89,0.06)', border: '0.5px solid rgba(52,199,89,0.25)',
                      borderRadius: 10, padding: '10px 12px', marginBottom: 8,
                    }}>
                      <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4 }}>
                        {formatBJTime(s.created_at)} · {s.title || '结算通知'}
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{s.content}</div>
                      <button className="btn btn-outline btn-sm" style={{ marginTop: 8, fontSize: 12, padding: '4px 12px' }}
                        onClick={() => copySettlement(s.content)}>
                        <Copy size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />复制内容
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <div style={secTitle}><Tag size={12} /> 我的单价</div>
                <div style={{ display: 'flex', gap: 8 }}>{['legacy', 'puzzle'].map(priceBox)}</div>
              </div>

              {earnings.recentRecords?.length > 0 ? (
                <div>
                  <div style={{ ...secTitle, display: 'block' }}>近期结算</div>
                  {earnings.recentRecords.map((r, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '9px 0', borderBottom: i < earnings.recentRecords.length - 1 ? '0.5px solid var(--c-border)' : 'none',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {r.settlement_date}
                          <span style={{
                            marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6,
                            color: 'var(--c-primary)', background: 'var(--c-primary-light)', verticalAlign: '1px',
                          }}>{BIZ_LABEL[r.business]}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                          {(r.count || 0) > 0 ? `${r.count} ${BIZ_UNIT[r.business]}` : ''}
                        </div>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-success)' }}>+¥{Number(r.total_amount).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, color: 'var(--c-text-tertiary)' }}>暂无结算记录</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* USDT 可提现余额卡（仅上级已参与 USDT 体系且本人已绑定地址时显示） */}
      {wdAvailable && wdAddress && (
        <div className="card" style={{ padding: '16px 18px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Wallet size={13} /> 可提现余额
            </div>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--c-text-tertiary)', padding: '2px 8px' }}
              onClick={async () => {
                setWdHistory(true);
                try { const r = await api.get('/withdrawal/history'); setWdHistoryList(r.data.list || []); } catch {}
              }}>
              提现记录
            </button>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--c-text)', letterSpacing: '-0.02em', marginBottom: 14 }}>
            ¥{wdBalance.toFixed(2)}
          </div>
          {wdFrozen > 0 && (
            <div style={{ fontSize: 12, color: 'var(--c-accent)', marginTop: -8, marginBottom: 12, lineHeight: 1.6 }}>
              另有 ¥{wdFrozen.toFixed(2)} 提现处理中（已从余额冻结；被拒绝会原额退回）
            </div>
          )}
          <button className="btn btn-primary"
            style={{ width: '100%', height: 44, fontSize: 15, fontWeight: 600, borderRadius: 12 }}
            disabled={wdBalance <= 0}
            onClick={async () => {
              setWdModal(true);
              setWdAmountCny('');
              setWdRate(null);
              setWdRateErr(false);
              try {
                const r = await api.get('/withdrawal/rate');
                setWdRate(r.data);
              } catch { setWdRateErr(true); }
            }}>
            申请提现
          </button>
        </div>
      )}

      {/* 申请提现弹窗 */}
      {wdModal && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 999 }}
          onClick={() => setWdModal(false)}>
          <div style={{ width: '100%', background: 'var(--c-surface)', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>申请提现</div>

            {wdRateErr && <div style={{ fontSize: 13, color: 'var(--c-danger)', marginBottom: 12 }}>汇率获取失败，请关闭后重试</div>}
            {wdRate && (
              <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginBottom: 16 }}>
                当前汇率 {wdRate.effectiveRate.toFixed(2)} CNY/USDT · 最低提现 100 USDT
              </div>
            )}

            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 6 }}>提现金额（元）</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input className="input" type="number" placeholder="请输入提现金额"
                value={wdAmountCny} onChange={e => setWdAmountCny(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-outline btn-sm" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                onClick={() => setWdAmountCny(String(wdBalance.toFixed(2)))}>
                全额
              </button>
            </div>

            {wdRate && wdAmountCny && Number(wdAmountCny) > 0 && (
              <div style={{ background: 'rgba(52,199,89,0.08)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--c-success)', marginBottom: 16 }}>
                预计到账 {(Number(wdAmountCny) / wdRate.effectiveRate).toFixed(2)} USDT
              </div>
            )}

            {Number(wdAmountCny) > wdBalance && (
              <div style={{ fontSize: 13, color: 'var(--c-danger)', marginBottom: 12 }}>
                余额不足，当前可提现 ¥{wdBalance.toFixed(2)}
              </div>
            )}
            <button className="btn btn-primary"
              style={{ width: '100%', height: 48, fontSize: 15, fontWeight: 600, borderRadius: 14 }}
              disabled={wdSubmitting || !wdRate || wdRateErr || !wdAmountCny || Number(wdAmountCny) <= 0 || Number(wdAmountCny) > wdBalance}
              onClick={async () => {
                setWdSubmitting(true);
                try {
                  await api.post('/withdrawal/apply', { amount_cny: Number(wdAmountCny) });
                  showToast('提现申请已提交，等待审批', 'success');
                  setWdModal(false);
                  fetchWdInfo();
                } catch (err) {
                  showToast(err.response?.data?.error || '提交失败', 'error');
                } finally { setWdSubmitting(false); }
              }}>
              {wdSubmitting ? '提交中…' : '确认提现'}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* 提现记录弹窗 */}
      {wdHistory && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 999 }}
          onClick={() => setWdHistory(false)}>
          <div style={{ width: '100%', background: 'var(--c-surface)', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>提现记录</div>
            {wdHistoryList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 14, color: 'var(--c-text-tertiary)' }}>暂无记录</div>
            ) : wdHistoryList.map(item => {
              const statusMap = {
                pending:   { label: '待审批', color: 'var(--c-accent)' },
                approved:  { label: '审批通过', color: 'var(--c-primary)' },
                rejected:  { label: '已拒绝', color: 'var(--c-danger)' },
                completed: { label: '已到账', color: 'var(--c-success)' },
              };
              const s = statusMap[item.status] || { label: item.status, color: 'var(--c-text-tertiary)' };
              return (
                <div key={item.id} style={{ borderBottom: '0.5px solid var(--c-border)', padding: '12px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>¥{item.amount_cny.toFixed(2)}</div>
                    <span style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 4 }}>
                    {item.amount_usdt} USDT · {item.created_at?.slice(0, 16)}
                  </div>
                  {item.reject_reason && (
                    <div style={{ fontSize: 12, color: 'var(--c-danger)', marginTop: 4 }}>拒绝原因：{item.reject_reason}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
